import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { fetchTermsOfService, fetchPrivacyPolicy, trackArbitrationClick, LegalDocument } from '@/src/services/legalService';
import { useAuth } from '@/src/contexts/AuthContext';

interface ScreenProps {
  onAccepted?: () => void;
  showAcceptButton?: boolean;
}

export const TermsOfServiceScreen: React.FC<ScreenProps> = ({ onAccepted, showAcceptButton = false }) => {
  const [terms, setTerms] = useState<LegalDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    let active = true;
    fetchTermsOfService().then(data => {
      if (active) {
        setTerms(data);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-[#3a3430]">
        <div className="w-12 h-12 border-2 border-white/20 border-t-[#F26522] rounded-full animate-spin mb-4"></div>
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/60 font-heading">Syncing Intelligence Protocol...</p>
      </div>
    );
  }

  return (
    <div className="bg-[#3a3430] text-white p-6 md:p-10 flex flex-col h-full animate-in fade-in duration-200">
      <header className="border-b border-white/[0.06] pb-4 mb-6 shrink-0 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F26522] block mb-1 font-heading">eNvesta AI Legal</span>
          <h2 className="text-2xl font-bold tracking-tight font-heading text-white leading-none">Terms of Service</h2>
        </div>
        <div className="text-left md:text-right">
          <span className="text-[10px] font-medium uppercase tracking-widest text-white/60 block font-heading">Version: {terms?.version || '1.1.0'}</span>
          <span className="text-[10px] text-white/40 block mt-0.5">Effective: {terms?.effectiveDate || '2026, June, 15th'}</span>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto max-h-[50vh] pr-2 text-sm text-white/70 leading-relaxed space-y-4 no-scrollbar border-b border-white/[0.06] mb-6">
        <div className="markdown-body prose prose-invert max-w-none">
          <ReactMarkdown
            components={{
              a: ({ href, children, ...props }) => {
                const isArbitrationLink = href === '/legal/arbitration';
                const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (isArbitrationLink) {
                    e.preventDefault();
                    // Track click analytics
                    trackArbitrationClick(user?.uid);
                    
                    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                    const isWebView = typeof window !== 'undefined' && (
                      /wv|WebView/i.test(navigator.userAgent) ||
                      window.location.protocol === 'file:' ||
                      window.location.protocol === 'capacitor:' ||
                      window.location.protocol === 'chrome-extension:' ||
                      !(window.location.protocol.startsWith('http'))
                    );

                    const webBaseUrl = 'https://ais-pre-vopsowpbp3uzghxbuk2jnu-38169599415.europe-west2.run.app';

                    if (isMobile || isWebView) {
                      window.location.href = `${webBaseUrl}/legal/arbitration`;
                    } else {
                      window.open(`${window.location.origin}/legal/arbitration`, '_blank');
                    }
                  }
                };
                return (
                  <a
                    href={href}
                    onClick={handleClick}
                    className="text-[#F26522] hover:text-[#F26522]/80 font-semibold underline cursor-pointer transition-colors"
                    {...props}
                  >
                    {children}
                  </a>
                );
              }
            }}
          >
            {terms?.content || ''}
          </ReactMarkdown>
        </div>
      </div>

      {showAcceptButton && onAccepted && (
        <div className="shrink-0 flex justify-end">
          <button
            onClick={onAccepted}
            className="px-6 py-3 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-[10px] font-bold uppercase tracking-widest rounded-lg transition-colors cursor-pointer font-heading"
          >
            Acknowledge Terms
          </button>
        </div>
      )}
    </div>
  );
};

export const PrivacyPolicyScreen: React.FC<ScreenProps> = ({ onAccepted, showAcceptButton = false }) => {
  const [policy, setPolicy] = useState<LegalDocument | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchPrivacyPolicy().then(data => {
      if (active) {
        setPolicy(data);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-[#3a3430]">
        <div className="w-12 h-12 border-2 border-white/20 border-t-[#F26522] rounded-full animate-spin mb-4"></div>
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/60 font-heading">Syncing Data Directives...</p>
      </div>
    );
  }

  return (
    <div className="bg-[#3a3430] text-white p-6 md:p-10 flex flex-col h-full animate-in fade-in duration-200">
      <header className="border-b border-white/[0.06] pb-4 mb-6 shrink-0 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F26522] block mb-1 font-heading">eNvesta AI Legal</span>
          <h2 className="text-2xl font-bold tracking-tight font-heading text-white leading-none">Privacy Policy</h2>
        </div>
        <div className="text-left md:text-right">
          <span className="text-[10px] font-medium uppercase tracking-widest text-white/60 block font-heading">Version: {policy?.version || '1.0.0'}</span>
          <span className="text-[10px] text-white/40 block mt-0.5">Effective: {policy?.effectiveDate || '2026, June, 15th'}</span>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto max-h-[50vh] pr-2 text-sm text-white/70 leading-relaxed space-y-4 no-scrollbar border-b border-white/[0.06] mb-6">
        <div className="markdown-body prose prose-invert max-w-none">
          <ReactMarkdown>{policy?.content || ''}</ReactMarkdown>
        </div>
      </div>

      {showAcceptButton && onAccepted && (
        <div className="shrink-0 flex justify-end">
          <button
            onClick={onAccepted}
            className="px-6 py-3 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-[10px] font-bold uppercase tracking-widest rounded-lg transition-colors cursor-pointer font-heading"
          >
            Acknowledge Privacy
          </button>
        </div>
      )}
    </div>
  );
};
